import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError, useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  Wallet,
  Users,
  CalendarCheck,
  Sparkles,
  Percent,
  Download,
  ShieldCheck,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ClientCalendar from "../components/ClientCalendar";
import MaintenancePanel from "../components/MaintenancePanel";
import ReviewsPanel from "../components/ReviewsPanel";
import ForecastPanel from "../components/ForecastPanel";

// Recharts style/layout objects — kept at module scope so React never sees a
// new reference on re-render (avoids unnecessary chart reflows).
const CHART_MARGIN = { left: 0, right: 10, top: 10, bottom: 0 };
const CHART_TOOLTIP_STYLE = { background: "#0A1128", border: "none", color: "#F4F1EB", fontSize: 12 };

const Metric = ({ icon: Icon, label, value, testid, accent }) => (
  <div
    className={`bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-7 flex flex-col gap-3 ${accent ? "ring-1 ring-[#C5A059]/40" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="overline text-[10px]">{label}</span>
      <Icon size={16} strokeWidth={1.3} className="text-[#C5A059]" />
    </div>
    <div className="font-serif-display text-3xl md:text-4xl font-light text-[#0A1128] leading-none">
      {value}
    </div>
  </div>
);

const MonthPicker = ({ value, onChange }) => {
  // Last 12 months
  const months = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
      arr.push({ key, label });
    }
    return arr;
  }, []);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-transparent border border-[#0A1128]/20 px-3 py-2 text-xs tracking-[0.18em] uppercase text-[#0A1128] hover:border-[#C5A059] focus:outline-none"
      data-testid="report-month-picker"
    >
      {months.map((m) => (
        <option key={m.key} value={m.key}>{m.label}</option>
      ))}
    </select>
  );
};

const DashboardPage = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [allHouses, setAllHouses] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState("all"); // "all" | houseId
  const [maint, setMaint] = useState([]);
  const [reviews, setReviews] = useState({ average: 0, count: 0, items: [] });
  const [forecast, setForecast] = useState({ horizons: [] });
  const [calendarData, setCalendarData] = useState({ occupied: [], bookings: [] });
  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const qs = selectedHouse === "all" ? "" : `?house_id=${selectedHouse}`;
    Promise.all([
      api.get(`/me/stats${qs}`),
      api.get(`/me/maintenances${qs}`).catch(() => ({ data: [] })),
      api.get(`/me/reviews${qs}`).catch(() => ({ data: { average: 0, count: 0, items: [] } })),
      api.get(`/me/forecast${qs}`).catch(() => ({ data: { horizons: [] } })),
      api.get(`/me/calendar${qs}`).catch(() => ({ data: { occupied: [], bookings: [] } })),
    ]).then(([s, m, r, f, c]) => {
      setStats(s.data);
      setMaint(m.data);
      setReviews(r.data);
      setForecast(f.data);
      setCalendarData(c.data);
      // Track the full list of houses only on first load (when viewing all)
      if (selectedHouse === "all" && Array.isArray(s.data?.houses)) {
        setAllHouses(s.data.houses);
      }
    });
  }, [selectedHouse]);

  const currency = useMemo(
    () =>
      new Intl.NumberFormat(lang === "pt" ? "pt-PT" : "en-US", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }),
    [lang]
  );

  const monthlyData = useMemo(() => {
    if (!stats?.monthly_earnings) return [];
    return stats.monthly_earnings.map((m) => ({ month: m.month.slice(5), amount: m.amount }));
  }, [stats]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const houseParam = selectedHouse === "all" ? "" : `&house_id=${selectedHouse}`;
      const res = await api.get(`/me/report-pdf?month=${reportMonth}${houseParam}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stayzen-relatorio-${reportMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório descarregado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!stats) {
    return (
      <div className="bg-[#F4F1EB] min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-[#3A435E]">…</div>
      </div>
    );
  }

  // Admins should never see the client dashboard — bounce them to /admin
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const hasMaintenance = !!(stats.houses || []).some((h) => h?.has_maintenance);

  return (
    <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
      <Navbar />
      <main
        className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-12 py-12 md:py-20"
        data-testid="client-dashboard"
      >
        {/* HEADER — luxurious banner */}
        <section className="relative bg-[#0A1128] text-[#F4F1EB] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07]"
               style={{ backgroundImage:
                "radial-gradient(circle at 20% 10%, #C5A059 0px, transparent 320px), radial-gradient(circle at 90% 80%, #C5A059 0px, transparent 280px)" }} />
          <div className="relative px-8 md:px-12 py-10 md:py-14 grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[#C5A059] mb-3">{t.dashboard.welcome}</div>
              <h1 className="font-serif-display text-4xl md:text-6xl font-light leading-tight">
                {user?.name?.split(" ")[0] || ""}
              </h1>
              <p className="mt-4 text-[#F4F1EB]/75 font-light max-w-xl">
                Tudo o que precisa de saber sobre {stats.houses && stats.houses.length > 1 ? "as suas casas" : "a sua casa"}, num só lugar. Total transparência sobre receitas,
                ocupação, manutenções e a opinião dos seus hóspedes.
              </p>
              <div className="flex items-center gap-2 mt-5 text-xs text-[#F4F1EB]/70">
                <ShieldCheck size={14} className="text-[#C5A059]" strokeWidth={1.4} />
                Dados privados, atualizados em tempo real
              </div>
            </div>
            {stats.houses && stats.houses.length > 0 && (
              <div className="md:col-span-5 md:text-right border-l md:border-l border-[#F4F1EB]/15 md:pl-8" data-testid="dashboard-portfolio-summary">
                {stats.houses.length === 1 ? (
                  <>
                    <div className="overline text-[#C5A059]">{stats.houses[0].location}</div>
                    <div className="font-serif-display text-2xl md:text-3xl font-light mt-1">
                      {stats.houses[0].name}
                    </div>
                    <div className="text-xs text-[#F4F1EB]/60 mt-3">
                      {stats.houses[0].bedrooms} quartos · {stats.houses[0].bathrooms} wc · {stats.houses[0].max_guests} hóspedes
                    </div>
                  </>
                ) : (
                  <>
                    <div className="overline text-[#C5A059]">Portefólio</div>
                    <div className="font-serif-display text-2xl md:text-3xl font-light mt-1">
                      {stats.houses.length} propriedades
                    </div>
                    <ul className="text-xs text-[#F4F1EB]/70 mt-3 space-y-1">
                      {stats.houses.map((h) => (
                        <li key={h.id} className="md:text-right">
                          <span className="text-[#C5A059]">·</span> {h.name} <span className="text-[#F4F1EB]/50">— {h.location}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {(!stats.houses || stats.houses.length === 0) && (
          <div className="mt-12 border border-[#C5A059]/40 bg-[#FCFAF8] p-10" data-testid="no-house-message">
            <p className="text-[#3A435E] font-light">{t.dashboard.no_house}</p>
          </div>
        )}

        {stats.houses && stats.houses.length > 0 && (
          <>
            {/* HOUSE SELECTOR (only when the client has more than one property) */}
            {allHouses.length > 1 && (
              <section
                className="mt-8 bg-[#FCFAF8] border border-[#0A1128]/10 p-4 md:p-5 flex flex-wrap items-center gap-3"
                data-testid="house-selector-bar"
              >
                <span className="overline text-[#C5A059] text-[10px]">
                  {lang === "pt" ? "A ver" : "Viewing"}
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedHouse("all")}
                    className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                      selectedHouse === "all"
                        ? "bg-[#0A1128] text-[#F4F1EB] border-[#0A1128]"
                        : "bg-transparent text-[#0A1128] border-[#0A1128]/20 hover:border-[#C5A059] hover:text-[#C5A059]"
                    }`}
                    data-testid="house-selector-all"
                  >
                    {lang === "pt" ? `Todas · ${allHouses.length}` : `All · ${allHouses.length}`}
                  </button>
                  {allHouses.map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => setSelectedHouse(h.id)}
                      className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                        selectedHouse === h.id
                          ? "bg-[#0A1128] text-[#F4F1EB] border-[#0A1128]"
                          : "bg-transparent text-[#0A1128] border-[#0A1128]/20 hover:border-[#C5A059] hover:text-[#C5A059]"
                      }`}
                      data-testid={`house-selector-${h.id}`}
                    >
                      {h.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Metrics */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
              <Metric icon={Wallet} accent label={t.dashboard.metrics.earnings} value={currency.format(stats.earnings)} testid="metric-earnings" />
              <Metric icon={CalendarCheck} label={t.dashboard.metrics.bookings} value={stats.bookings_count} testid="metric-bookings" />
              <Metric icon={Users} label={t.dashboard.metrics.guests} value={stats.total_guests} testid="metric-guests" />
              <Metric icon={Sparkles} label={t.dashboard.metrics.cleanings} value={stats.cleanings_count} testid="metric-cleanings" />
              <Metric icon={Percent} label={t.dashboard.metrics.occupancy} value={`${stats.occupancy_rate}%`} testid="metric-occupancy" />
            </div>

            {/* Monthly PDF report bar */}
            <section className="mt-8 bg-[#FCFAF8] border border-[#C5A059]/40 p-6 md:p-7 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="overline text-[#C5A059]">Relatório mensal</div>
                <h3 className="font-serif-display text-xl md:text-2xl text-[#0A1128] mt-1">
                  Descarregue o seu PDF
                </h3>
                <p className="text-xs text-[#3A435E] mt-1">
                  Receita, ocupação, limpezas, manutenções e avaliações do mês — tudo em formato profissional.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <MonthPicker value={reportMonth} onChange={setReportMonth} />
                <button
                  onClick={downloadReport}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
                  data-testid="download-report-button"
                >
                  <Download size={14} strokeWidth={1.6} />
                  {downloading ? "A gerar…" : "Descarregar PDF"}
                </button>
              </div>
            </section>

            {/* TABS */}
            <Tabs defaultValue="overview" className="mt-10">
              <TabsList
                className="bg-[#FCFAF8] border border-[#0A1128]/10 p-0 h-auto gap-0 rounded-none w-full md:w-auto inline-flex"
                data-testid="client-tabs"
              >
                {[
                  { k: "overview", l: "Visão geral" },
                  { k: "calendar", l: "Calendário" },
                  ...(hasMaintenance ? [{ k: "maintenance", l: "Manutenção" }] : []),
                  { k: "reviews", l: "Avaliações" },
                ].map(({ k, l }) => (
                  <TabsTrigger
                    key={k}
                    value={k}
                    className="data-[state=active]:bg-[#0A1128] data-[state=active]:text-[#F4F1EB] rounded-none px-6 py-3 text-xs tracking-[0.2em] uppercase"
                    data-testid={`client-tab-${k}`}
                  >
                    {l}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* OVERVIEW */}
              <TabsContent value="overview" className="mt-8 space-y-8">
                <ForecastPanel data={forecast} />

                <div className="grid md:grid-cols-12 gap-8">
                  <section
                    className="md:col-span-7 bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8 min-w-0"
                    data-testid="chart-section"
                  >
                    <div className="flex items-center justify-between">
                      <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
                        {t.dashboard.earnings_chart}
                      </h2>
                    </div>
                    <div className="mt-6 h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyData} margin={CHART_MARGIN}>
                          <defs>
                            <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#C5A059" stopOpacity={0.45} />
                              <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="0" stroke="#0A112810" vertical={false} />
                          <XAxis dataKey="month" stroke="#3A435E" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis stroke="#3A435E" fontSize={12} tickLine={false} axisLine={false} />
                          <Tooltip
                            contentStyle={CHART_TOOLTIP_STYLE}
                            formatter={(value) => currency.format(value)}
                          />
                          <Area type="monotone" dataKey="amount" stroke="#C5A059" strokeWidth={2} fill="url(#gold)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </section>

                  <section
                    className="md:col-span-5 bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8"
                    data-testid="recent-bookings-section"
                  >
                    <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
                      {t.dashboard.recent_bookings}
                    </h2>
                    <div className="mt-6 divide-y divide-[#0A1128]/10 -mx-2">
                      {stats.recent_bookings.length === 0 ? (
                        <p className="text-[#3A435E] text-sm px-2 py-4">{t.dashboard.empty}</p>
                      ) : (
                        stats.recent_bookings.map((b) => (
                          <div
                            key={b.id}
                            className="px-2 py-4 flex items-start justify-between gap-4"
                            data-testid={`booking-row-${b.id}`}
                          >
                            <div>
                              <div className="text-[#0A1128] font-medium">{b.guest_name}</div>
                              <div className="text-xs text-[#3A435E] mt-1">
                                {b.check_in} → {b.check_out} · {b.nights}n · {b.guests_count} {t.houses.guests}
                              </div>
                            </div>
                            <div className="font-serif-display text-lg text-[#C5A059]">
                              {currency.format(b.amount)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </div>
              </TabsContent>

              {/* CALENDAR */}
              <TabsContent value="calendar" className="mt-8">
                <div className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8">
                  <ClientCalendar occupied={calendarData.occupied} />
                </div>
              </TabsContent>

              {/* MAINTENANCE */}
              {hasMaintenance && (
                <TabsContent value="maintenance" className="mt-8">
                  <div className="mb-5">
                    <div className="overline text-[#C5A059]">Plano de manutenção ativo</div>
                    <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128] mt-1">
                      Intervenções na sua casa
                    </h2>
                    <p className="text-sm text-[#3A435E] mt-2 max-w-2xl">
                      Cada manutenção é registada com fotografias do antes/depois, descrição da intervenção,
                      custo e estado, para que tenha total visibilidade do que acontece em sua casa.
                    </p>
                  </div>
                  <MaintenancePanel items={maint} />
                </TabsContent>
              )}

              {/* REVIEWS */}
              <TabsContent value="reviews" className="mt-8">
                <div className="mb-5">
                  <div className="overline text-[#C5A059]">A voz dos seus hóspedes</div>
                  <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128] mt-1">
                    Avaliações recebidas
                  </h2>
                </div>
                <ReviewsPanel data={reviews} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default DashboardPage;
